import { queryOptions, useQuery } from '@tanstack/react-query'
import { useUserId } from '@/features/account/services/User/hooks'
import { fetchProductNotes, fetchProductNote } from '@/features/shop/services/ProductNote/fetchers'
import { type BulkProductNotesRequestItem } from '@/features/shop/services/ProductNote/types'
import { type Product } from '@/features/shop/services/Product/types'

type Params = { userId: number; categoryId: number; productId: number }
export type ProductNoteQueryKey = readonly [string, Params]

export const productNoteQueryOptions = (params: Params) =>
  queryOptions({
    queryKey: ['product note', params] as const,
    queryFn: fetchProductNote,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

export const useQueryProductNote = ({ productId, categoryId }: Omit<Params, 'userId'>) => {
  const userId = useUserId() ?? 0

  return useQuery({
    ...productNoteQueryOptions({ userId, categoryId, productId }),
    enabled: Boolean(productId && categoryId),
  })
}

export const BulkProductNoteQuery = 'product notes'
export type BulkProductNoteQueryKey = readonly [
  typeof BulkProductNoteQuery,
  { userId: number; products: BulkProductNotesRequestItem[] },
]

export const bulkProductNoteQueryOptions = (userId: number, products: Product[]) =>
  queryOptions({
    queryKey: [
      BulkProductNoteQuery,
      {
        userId,
        // react-query hashes the query key with hashKey, which sorts plain object keys
        // but leaves array order alone so [a, b] and [b, a] are different cache entries.
        // this is to dedupe and sort by id so the same set of products always hits the same entry.
        products: [...new Map(products.map((p) => [p.id, p])).values()]
          .sort((a, b) => a.id - b.id)
          .map((p) => ({ product_id: p.id, category_id: p.rootCategoryId })),
      },
    ],
    queryFn: fetchProductNotes,
  })
